import { FC, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';

type Variant = 'banner' | 'card';

type Props = {
  label?: string;
  isInitialized?: boolean;
  hideNumbers?: boolean;
  days?: number;
  hours: number;
  minutes: number;
  seconds?: number;
  variant: Variant;
}

const UNIT_SUFFIX: Record<string, string> = {
  days: 'd',
  hours: 'h',
  mins: 'm',
  seconds: 's',
};

const pc = (opacity: number) =>
  `color-mix(in srgb, var(--primaryColor) ${opacity}%, transparent)`;

const Countdown:FC<Props> = ({ label, isInitialized = false, hideNumbers = false, days, hours, minutes, seconds, variant }) => {
  const { t } = useTranslation();

  const getValue = (value:number) => {
    if (!isInitialized) return '--';
    return value < 10 ? `0${value}` : `${value}`;
  }

  const secondsDefined = seconds !== undefined;
  const showDays = !!(days || !secondsDefined);
  const showSeconds = !days && secondsDefined;
  const compactMode = (days || 0) >= 100;

  const units = [
    ...(showDays ? [{ value: days || 0, labelKey: 'days' as const }] : []),
    { value: hours, labelKey: 'hours' as const },
    ...(!compactMode ? [{ value: minutes, labelKey: 'mins' as const }] : []),
    ...(showSeconds ? [{ value: seconds ?? 0, labelKey: 'seconds' as const }] : []),
  ];

  const isBanner = variant === 'banner';

  const pill = (children: React.ReactNode) => (
    <div
      className={`w-full flex items-center overflow-hidden ${!isBanner ? 'shadow-primary' : ''}`}
      style={isBanner ? {
        background: `
          radial-gradient(ellipse 60% 160% at 100% 50%, ${pc(20)} 0%, transparent 65%),
          rgba(4, 6, 28, 0.92)
        `,
        borderBottom: `2px solid var(--primaryColor)`,
        boxShadow: `0 3px 20px ${pc(50)}, 0 6px 40px ${pc(25)}`,
      } : {
        background: `rgba(4, 6, 28, 0.85)`,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: '16px',
        border: `1px solid ${pc(25)}`,
      }}
    >
      {isBanner && (
        <div
          className="flex-shrink-0"
          style={{
            alignSelf: 'stretch',
            margin: '8px 12px 8px 14px',
            width: '3px',
            background: `linear-gradient(180deg, var(--primaryColor) 0%, ${pc(25)} 100%)`,
            boxShadow: `0 0 10px var(--primaryColor), 0 0 4px var(--primaryColor)`,
            borderRadius: '99px',
          }}
        />
      )}
      {children}
    </div>
  );

  if (!isInitialized) return null;

  if (hideNumbers) {
    return label ? pill(
      <div className="flex-1 flex items-center justify-center py-3 px-4">
        <h2 className="text-sm font-semibold uppercase text-white text-center" style={{ letterSpacing: '0.15em' }}>
          <Trans t={t} i18nKey={label} components={{ br: <> </> }} />
        </h2>
      </div>
    ) : null;
  }

  return pill(
    <div className={`flex items-center py-[9px] w-full gap-2 ${isBanner ? 'pr-3' : 'px-4'}`}>

      {label ? (
        <span
          className="text-xs font-semibold uppercase text-white whitespace-nowrap flex-shrink-0"
          style={{ letterSpacing: '0.12em' }}
        >
          <Trans t={t} i18nKey={label} components={{ br: <> </> }} />
        </span>
      ) : null}

      {label ? (
        <div
          className="flex-shrink-0"
          style={{
            width: '1px',
            alignSelf: 'stretch',
            margin: '2px 0',
            background: `linear-gradient(180deg, transparent, ${pc(50)}, transparent)`,
          }}
        />
      ) : null}

      <div className={`flex items-center gap-[3px] flex-shrink-0 ${label ? 'ml-auto' : 'mx-auto'}`}>
        {units.map((unit, idx) => (
          <Fragment key={unit.labelKey}>
            {idx > 0 && (
              <span className="text-[10px] font-bold leading-none flex-shrink-0" style={{ color: pc(35) }}>·</span>
            )}
            <div
              style={{
                padding: '3px 7px 4px',
                background: `linear-gradient(160deg, ${pc(12)} 0%, rgba(4, 6, 28, 0.9) 60%)`,
                border: `1px solid ${pc(38)}`,
                borderRadius: '7px',
                flexShrink: 0,
                boxShadow: `
                  0 0 12px ${pc(18)},
                  inset 0 1px 0 ${pc(20)},
                  inset 0 -1px 0 rgba(0,0,0,0.3)
                `,
              }}
            >
              <span className="flex items-baseline gap-[1px]">
                <span
                  className="text-white leading-none tabular-nums"
                  style={{
                    fontFamily: 'var(--primaryFont), sans-serif',
                    fontWeight: 700,
                    fontSize: 'clamp(12px, 3.8vw, 20px)',
                    textShadow: `0 0 8px ${pc(90)}, 0 0 20px ${pc(50)}`,
                  }}
                >
                  {getValue(unit.value)}
                </span>
                <span
                  className="font-semibold leading-none mb-[1px]"
                  style={{ fontSize: '9px', color: 'rgba(160, 160, 160, 0.9)' }}
                >
                  {UNIT_SUFFIX[unit.labelKey]}
                </span>
              </span>
            </div>
          </Fragment>
        ))}
      </div>

    </div>
  );
}

export default Countdown;
